import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Mail, X, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { productService, categoryService, paramService, emailService } from '@/services/storage';
import type { Product, ProductCategory, ProductParam } from '@/types';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [category, setCategory] = useState<ProductCategory | null>(null);
  const [params, setParams] = useState<ProductParam[]>([]);
  const [prevProduct, setPrevProduct] = useState<Product | null>(null);
  const [nextProduct, setNextProduct] = useState<Product | null>(null);

  // Contact modal state
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      if (id) {
        const prod = await productService.getById(id);
        if (prod) {
          setProduct(prod);
          const cat = await categoryService.getById(prod.categoryId);
          setCategory(cat || null);
          setParams(await paramService.getByCategory(prod.categoryId));

          // Get prev/next products
          const allProducts = await productService.getByCategory(prod.categoryId);
          const currentIndex = allProducts.findIndex((p) => p.id === id);
          setPrevProduct(currentIndex > 0 ? allProducts[currentIndex - 1] : null);
          setNextProduct(
            currentIndex < allProducts.length - 1 ? allProducts[currentIndex + 1] : null
          );
        }
      }
    };
    loadProduct();
  }, [id]);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailService.sendEmail({
        to: 'sales@hypwr.com',
        subject: `产品咨询 - ${product?.model || ''}`,
        body: `姓名: ${contactForm.name}\n邮箱: ${contactForm.email}\n电话: ${contactForm.phone}\n\n留言:\n${contactForm.message}`,
        from: contactForm.email
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        setIsContactModalOpen(false);
        setSubmitSuccess(false);
        setContactForm({ name: '', email: '', phone: '', message: '' });
      }, 2000);
    } catch (error) {
      alert('发送失败，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!product) {
    return (
      <div className="pt-32 pb-20 text-center">
        <div className="text-gray-500">产品不存在</div>
        <Button asChild className="mt-4">
          <Link to="/products">返回产品列表</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Breadcrumb */}
      <section className="bg-gray-50 py-4 border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600">
              首页
            </Link>
            <span className="mx-2">/</span>
            <Link to="/products" className="hover:text-blue-600">
              产品中心
            </Link>
            <span className="mx-2">/</span>
            {category && (
              <>
                <Link
                  to={`/products?category=${category.id}`}
                  className="hover:text-blue-600"
                >
                  {category.name}
                </Link>
                <span className="mx-2">/</span>
              </>
            )}
            <span className="text-gray-900">{product.model}</span>
          </div>
        </div>
      </section>

      {/* Product Header */}
      <section className="py-12 bg-gradient-to-r from-blue-900 to-blue-700">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <Badge className="mb-4 bg-white/20 text-white border-0">
                {category?.name}
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                {product.model}
              </h1>
            </div>
            <div className="mt-6 md:mt-0 flex space-x-4">
              <Button
                asChild
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-gray-100"
              >
                <Link to="/sample-apply">
                  <Mail className="mr-2 h-5 w-5" />
                  申请样品
                </Link>
              </Button>
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Download className="mr-2 h-5 w-5" />
                下载规格书
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-6">产品参数</h2>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <tbody>
                        {params.map((param, index) => (
                          <tr
                            key={param.key}
                            className={index % 2 === 0 ? 'bg-gray-50' : ''}
                          >
                            <td className="py-3 px-4 font-medium text-gray-700 w-1/3">
                              {param.label}
                              {param.unit && (
                                <span className="text-xs text-gray-400 ml-1">
                                  ({param.unit})
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-900">
                              {product.parameters[param.key] !== undefined
                                ? String(product.parameters[param.key])
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Description */}
              {product.description && (
                <Card className="mt-6">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4">产品描述</h2>
                    <p className="text-gray-600">{product.description}</p>
                  </CardContent>
                </Card>
              )}

              {/* Applications */}
              {product.applications && product.applications.length > 0 && (
                <Card className="mt-6">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold mb-4">典型应用</h2>
                    <div className="flex flex-wrap gap-2">
                      {product.applications.map((app) => (
                        <Badge key={app} variant="secondary">
                          {app}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div>
              {/* Contact Card */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-4">需要更多信息？</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    我们的技术团队随时为您提供支持和咨询服务。
                  </p>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => setIsContactModalOpen(true)}
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    联系我们
                  </Button>
                </CardContent>
              </Card>

              {/* Sample Apply Card */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2 text-blue-900">样品申请</h3>
                  <p className="text-blue-700 text-sm mb-4">
                    收到您的预约咨询后将会与您进行深度需求沟通。
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full border-blue-600 text-blue-600 hover:bg-blue-100"
                  >
                    <Link to="/sample-apply">立即申请</Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-8 bg-gray-50 border-t">
        <div className="container mx-auto px-4">
          <div className="flex justify-between">
            {prevProduct ? (
              <Link
                to={`/products/${prevProduct.id}`}
                className="flex items-center text-gray-600 hover:text-blue-600"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                <div>
                  <div className="text-sm text-gray-400">上一个</div>
                  <div className="font-medium">{prevProduct.model}</div>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {nextProduct ? (
              <Link
                to={`/products/${nextProduct.id}`}
                className="flex items-center text-gray-600 hover:text-blue-600 text-right"
              >
                <div>
                  <div className="text-sm text-gray-400">下一个</div>
                  <div className="font-medium">{nextProduct.model}</div>
                </div>
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      {isContactModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">联系我们</h3>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">发送成功！</h4>
                  <p className="text-gray-600">我们会尽快与您联系</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">姓名 *</Label>
                    <Input
                      id="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="请输入您的姓名"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">邮箱 *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="请输入您的邮箱"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">电话</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      placeholder="请输入您的电话（选填）"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">留言 *</Label>
                    <Textarea
                      id="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="请输入您想咨询的内容..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="text-sm text-gray-500">
                    咨询产品：{product?.model}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        发送中...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Send className="mr-2 h-4 w-4" />
                        发送咨询
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
